// src/router/index.ts
import { createRouter, createWebHistory } from 'vue-router';
import { removeEventListeners } from '@/classic/JqueryOverrides';
import { webRoutes, PerformWebRules, WebAfterRules } from '@/router/webRoutes';
import { startTimer, stopTimer, resetTimers, setPageContext } from '@/services/admin_actions/pageTimerService';
import { App as CapacitorApp } from '@capacitor/app';
import { setNativeToken, initTokens } from '@/services/apiServiceCSRF';
import { useUserDataStore } from '@/store/userDataStore';
import { isMobileApp } from '@/services/mobile_app/mobileAppServices';

import adminRoutes from '@/router/adminRoutes';
import settingsRoutes from '@/router/settingsRoutes';
import federerationRoute from '@/router/federationRoute';

declare let $: any;

const routes = [...webRoutes, ...adminRoutes, ...federerationRoute, ...settingsRoutes];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0, behavior: 'smooth' };
  },
});

// Stores the SSO JWT in native secure storage and refreshes app auth state.
// Returns true if an token param was found and handled.
async function extractSsoJwt(params: URLSearchParams): Promise<boolean> {
  const ssoJwt = params.get('token');
  if (!ssoJwt) return false;
  await setNativeToken(ssoJwt);
  await initTokens();
  useUserDataStore().fetchUserData();
  return true;
}

// Deep link handler — only active inside the native app
if (isMobileApp()) {
  CapacitorApp.addListener('appUrlOpen', async (event) => {
    // e.g. squashlevels://player_detail?id=123  or  https://squashlevels.com/player?id=123&token=...
    const url = new URL(event.url);
    await extractSsoJwt(url.searchParams);
    url.searchParams.delete('token');

    const path = url.pathname || url.host; // host covers squashlevels://player_detail (no slashes)
    const query = url.searchParams.toString() ? `?${url.searchParams.toString()}` : '';

    if (path) {
      router.push(`/${path}${query}`.replace('//', '/'));
    }
  });

  // appUrlOpen only fires for subsequent opens, not the cold-start URL.
  // getLaunchUrl handles the case where the app was launched via a universal link.
  CapacitorApp.getLaunchUrl().then(async (result) => {
    if (!result?.url) return;
    const url = new URL(result.url);
    const hadSsoJwt = await extractSsoJwt(url.searchParams);
    if (hadSsoJwt) {
      url.searchParams.delete('token');
      const path = url.pathname || url.host;
      const query = url.searchParams.toString() ? `?${url.searchParams.toString()}` : '';
      router.replace(`/${path}${query}`.replace('//', '/'));
    }
  });
}

router.beforeEach(async (to, from) => {
  if (to.query.token) {
    const { token, ...query } = to.query;
    return { path: to.path, query, replace: true };
  }

  resetTimers();
  setPageContext(to.fullPath);
  startTimer('router');

  let redirect = await PerformWebRules(to, from);

  removeEventListeners();

  if (redirect != undefined)
    return redirect;

});

router.afterEach(async (to, from) => {
  stopTimer('router');
  await WebAfterRules(to, from);

  if (!document.getElementById('external-content')) {
    setTimeout(() => {
      const event = new Event('body_content_loaded');
      document.dispatchEvent(event);
    }, 100);
  }
});

export default router;
